import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { useSession } from '../../app/providers/SessionProvider';
import { apiFetch } from '../../api/apiFetch';
import HeroDiagram from './HeroDiagram';
import './hero-landing.css';

/**
 * The way in.
 *
 * One screen, no scrollbar on a desktop, no sign-in, one door. Everything here
 * is in service of a reader who has never seen the product deciding whether to
 * press Enter — so it says what the product is *for* rather than what it
 * contains, and it names no carrier, no estate and no figure. This page is
 * shown to people whose own estate is the subject; a headline about somebody
 * else's application count is furniture, and a diagram of the model is not.
 *
 * The accent picker is not decoration. The shell is tinted by a single custom
 * property, and letting someone choose it here means they arrive at a product
 * that already looks like theirs. The choice is written to the person's
 * preferences, not to the browser, so it follows them to another machine.
 */

/**
 * Each accent carries two canvases, not one.
 *
 * A single value was applied in both modes, so choosing emerald in dark mode
 * painted a *light* green canvas (#e6f2ea) under dark surfaces and dark text.
 * The dark canvases below are the near-black base nudged toward the hue —
 * still dark enough that translucent surfaces have something to refract.
 */
const ACCENTS: Array<{
  id: string;
  label: string;
  from: string;
  to: string;
  light: string;
  dark: string;
}> = [
  { id: 'aurora', label: 'Aurora', from: '#38bdf8', to: '#6366f1', light: '#e7ecf5', dark: '#070b14' },
  { id: 'indigo', label: 'Indigo', from: '#818cf8', to: '#4338ca', light: '#e9e9f7', dark: '#08091a' },
  { id: 'violet', label: 'Violet', from: '#c084fc', to: '#7c3aed', light: '#efe9f8', dark: '#0d0818' },
  { id: 'teal', label: 'Teal', from: '#5eead4', to: '#0d9488', light: '#e4f2f0', dark: '#04120f' },
  { id: 'emerald', label: 'Emerald', from: '#6ee7b7', to: '#059669', light: '#e6f2ea', dark: '#05120c' },
  { id: 'amber', label: 'Amber', from: '#fcd34d', to: '#d97706', light: '#f6efe1', dark: '#140f04' },
  { id: 'rose', label: 'Rose', from: '#fda4af', to: '#e11d48', light: '#f7e9ec', dark: '#150609' },
  { id: 'slate', label: 'Slate', from: '#cbd5e1', to: '#475569', light: '#e9edf2', dark: '#0a0e14' },
];

/** Where the resolved accent is cached so it can be applied before first paint. */
export const ACCENT_CACHE_KEY = 'aurora.pvi.accent.v1';

/**
 * Three statements, no supporting paragraph.
 *
 * The explanatory text under each of these was doing the diagram's job in
 * words, and doing it worse. With the schematic beside them these can be what
 * they should have been: short enough to read at a glance and set large enough
 * to be read from the back of a room.
 */
const PROMISES = [
  'Every product, in one estate',
  'Money attached to outcomes',
  'Nothing invented, ever',
];


/**
 * Paints the accent, and remembers it for the next first paint.
 *
 * Both canvases are set together and the stylesheet picks whichever the
 * current mode calls for, so a light/dark toggle never has to re-apply the
 * accent — and can never end up with the wrong one.
 */
export function applyAccent(accentId: string): void {
  const accent = ACCENTS.find((entry) => entry.id === accentId) ?? ACCENTS[0]!;
  const root = document.documentElement;
  root.style.setProperty('--g-accent', accent.to);
  root.style.setProperty('--g-aurora-1', accent.from);
  root.style.setProperty('--g-aurora-2', accent.to);
  // Only the canvas is tinted. Surfaces keep their own colour so the figures
  // sitting on them hold their contrast whichever accent is chosen.
  root.style.setProperty('--g-canvas-light', accent.light);
  root.style.setProperty('--g-canvas-dark', accent.dark);

  try {
    window.localStorage.setItem(
      ACCENT_CACHE_KEY,
      JSON.stringify({
        id: accent.id,
        accent: accent.to,
        from: accent.from,
        light: accent.light,
        dark: accent.dark,
      }),
    );
  } catch {
    // A browser refusing storage costs a one-frame flash, nothing more.
  }
}

export default function HeroLandingPage() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [accent, setAccent] = useState('aurora');
  const { currentUser, availableUsers, switchUser } = useSession();

  useEffect(() => {
    let cancelled = false;
    apiFetch(`${import.meta.env.VITE_API_BASE_URL}/me/preferences`)
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { data?: { shell_accent?: string } } | null) => {
        const saved = body?.data?.shell_accent;
        if (!cancelled && saved) {
          setAccent(saved);
          applyAccent(saved);
        }
      })
      .catch(() => {
        // A preference that cannot be read is not worth an error on the way in.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = (id: string) => {
    setAccent(id);
    applyAccent(id);
    void apiFetch(`${import.meta.env.VITE_API_BASE_URL}/me/preferences`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ shell_accent: id }),
    }).catch(() => {
      // The colour is already applied; failing to persist it is not worth
      // interrupting someone who is trying to get into the product.
    });
  };

  const current = ACCENTS.find((entry) => entry.id === accent) ?? ACCENTS[0]!;

  return (
    /* The wash variables live on the root, not on the background layer: the
       title's gradient text and the Enter button both read them, and scoped to
       .hero-wash they resolved to nothing — which rendered the emphasised word
       transparent on a transparent background, i.e. invisible. */
    <div
      className="hero-root"
      style={
        {
          '--wash-from': current.from,
          '--wash-to': current.to,
        } as React.CSSProperties
      }
    >
      <div className="hero-wash" aria-hidden="true">
        <span className="hero-orb hero-orb-a" />
        <span className="hero-orb hero-orb-b" />
        <span className="hero-orb hero-orb-c" />
        <span className="hero-grid" />
      </div>

      <main className="hero-stage">
        <div className="hero-copy">
          <motion.header
            className="hero-head"
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="hero-eyebrow">Enterprise IT Intelligence</span>
            <h1 className="hero-title">
              Know what your <em>technology</em> is actually worth.
            </h1>
          </motion.header>

          <motion.ul
            className="hero-promises"
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            {PROMISES.map((promise, index) => (
              <motion.li
                key={promise}
                className="hero-card"
                initial={reduceMotion ? false : { opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.55, delay: 0.16 + index * 0.11, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="hero-card-rule" aria-hidden="true" />
                <span className="hero-card-title">{promise}</span>
              </motion.li>
            ))}
          </motion.ul>

          <motion.div
            className="hero-foot"
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="hero-actions">
              <div className="hero-swatches" role="group" aria-label="Shell colour">
                {ACCENTS.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => choose(entry.id)}
                    aria-label={entry.label}
                    aria-pressed={entry.id === accent}
                    title={entry.label}
                    className={`hero-swatch${entry.id === accent ? ' is-active' : ''}`}
                    style={{ background: `linear-gradient(135deg, ${entry.from}, ${entry.to})` }}
                  />
                ))}
              </div>

              {/*
                Enter *as* somebody. The role decides what the product is: a
                Product Owner gets one product's worth of estate, the CIO the
                whole portfolio without the administration screens, the
                administrator everything. Choosing here rather than hunting for
                a switcher afterwards makes that the first thing the platform
                says about itself.

                No password — there is no identity provider in this
                environment, and a login box that accepts anything would be
                theatre. The header names a person; the server decides what
                that person may see.
              */}
              <label className="hero-role">
                <span className="sr-only">Enter as</span>
                <select
                  value={currentUser.id}
                  onChange={(event) => switchUser(event.target.value)}
                  title="Each role sees a different portfolio."
                >
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.roleLabel}
                    </option>
                  ))}
                </select>
              </label>

              <button type="button" className="hero-enter" onClick={() => navigate('/v2/executive')}>
                Enter
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M5 12h14M13 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </motion.div>
        </div>

        {/* The diagram is the load-bearing explanation on this page, so it is
            not hidden from assistive technology — it carries a description. */}
        <motion.div
          className="hero-visual"
          initial={reduceMotion ? false : { opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
        >
          <HeroDiagram animate={!reduceMotion} />
        </motion.div>
      </main>
    </div>
  );
}
